import { useCallback, useEffect, useState } from 'react';
import { formatearMonedaArs, type ReporteFacturacionDTO } from '@ausentia/shared';
import { api, ErrorApi } from '../api/cliente.js';

/** Primer y último día del mes actual (AAAA-MM-DD), por defecto del período. */
function mesActual(): { desde: string; hasta: string } {
  const hoy = new Date();
  const primero = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const ultimo = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { desde: iso(primero), hasta: iso(ultimo) };
}

export function Facturacion() {
  const inicial = mesActual();
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [reporte, setReporte] = useState<ReporteFacturacionDTO | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const calcular = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      setReporte(await api.get<ReporteFacturacionDTO>(`/api/operador/facturacion?desde=${desde}&hasta=${hasta}`));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo calcular la facturación');
      setReporte(null);
    } finally {
      setCargando(false);
    }
  }, [desde, hasta]);

  useEffect(() => {
    void calcular();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sinValores = reporte
    ? reporte.visitasSinTarifa.length + reporte.visitasSinHonorario.length
    : 0;

  const margenPorcentaje = reporte && reporte.totalACobrarCentavos > 0
    ? ((reporte.gananciaCentavos / reporte.totalACobrarCentavos) * 100).toFixed(1)
    : '0.0';

  return (
    <section>
      <h2>Facturación del período</h2>
      <p className="muted">
        Cuánto cobrás a las empresas, cuánto pagás a los profesionales y cuánto te queda. La unidad es
        la visita atendida (no se cuentan dos veces las rectificaciones).
      </p>

      <div className="filtro-periodo">
        <div className="campo">
          <label htmlFor="desde">Desde</label>
          <input id="desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="hasta">Hasta</label>
          <input id="hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <button className="primario" style={{ width: 'auto' }} onClick={() => void calcular()} disabled={cargando}>
          {cargando ? 'Calculando…' : 'Calcular'}
        </button>
      </div>

      {error && <div className="error-box" role="status">{error}</div>}
      {cargando && !reporte && <div className="cargando">Calculando…</div>}

      {reporte && (
        <>
          <div className="grid-tarjetas" style={{ marginTop: 18 }}>
            <div className="kpi">
              <span className="kpi-rotulo">A cobrar (empresas)</span>
              <span className="kpi-monto">{formatearMonedaArs(reporte.totalACobrarCentavos)}</span>
            </div>
            <div className="kpi">
              <span className="kpi-rotulo">A pagar (profesionales)</span>
              <span className="kpi-monto">{formatearMonedaArs(reporte.totalAPagarCentavos)}</span>
            </div>
            <div className="kpi kpi-ganancia">
              <span className="kpi-rotulo">Ganancia</span>
              <span className="kpi-monto">{formatearMonedaArs(reporte.gananciaCentavos)}</span>
            </div>
            <div className="kpi">
              <span className="kpi-rotulo">Margen Clínico</span>
              <span className="kpi-monto">{margenPorcentaje}%</span>
            </div>
          </div>
          <p className="mini" style={{ marginTop: 6 }}>{reporte.visitasFacturables} visita(s) facturable(s) en el período.</p>

          {sinValores > 0 && (
            <div className="aviso-info" style={{ marginTop: 12, marginBottom: 16 }}>
              <span>⚠️</span>
              <span>
                Hay {reporte.visitasSinTarifa.length} visita(s) sin tarifa y {reporte.visitasSinHonorario.length} sin
                honorario configurado: no se computan hasta que las definas en Tarifas/Honorarios.
              </span>
            </div>
          )}

          {reporte && reporte.visitasSinTarifa.length > 0 && (
            <div style={{ marginTop: 16, marginBottom: 20 }}>
              <h4>Detalle de Visitas sin Tarifa</h4>
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Empresa</th>
                    <th>Tipo de Control</th>
                    <th>Médico</th>
                  </tr>
                </thead>
                <tbody>
                  {reporte.visitasSinTarifa.map((v) => (
                    <tr key={v.controlId}>
                      <td>{v.fechaControl.slice(0, 10)}</td>
                      <td>{v.empresaNombre}</td>
                      <td>{v.tipoControlNombre}</td>
                      <td>{v.medicoNombre}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {reporte && reporte.visitasSinHonorario.length > 0 && (
            <div style={{ marginTop: 16, marginBottom: 20 }}>
              <h4>Detalle de Visitas sin Honorario</h4>
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Médico</th>
                    <th>Empresa</th>
                    <th>Tipo de Control</th>
                  </tr>
                </thead>
                <tbody>
                  {reporte.visitasSinHonorario.map((v) => (
                    <tr key={v.controlId}>
                      <td>{v.fechaControl.slice(0, 10)}</td>
                      <td>{v.medicoNombre}</td>
                      <td>{v.empresaNombre}</td>
                      <td>{v.tipoControlNombre}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3 style={{ marginTop: 24 }}>A cobrar por empresa</h3>
          {reporte.porEmpresa.length === 0 ? (
            <p className="muted">No hay visitas en el período.</p>
          ) : (
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Empresa</th>
                  <th scope="col">Visitas</th>
                  <th scope="col">Sin tarifa</th>
                  <th scope="col">A cobrar</th>
                </tr>
              </thead>
              <tbody>
                {reporte.porEmpresa.map((e) => (
                  <tr key={e.empresaId}>
                    <td>{e.empresaNombre}</td>
                    <td>{e.visitas}</td>
                    <td>{e.visitasSinTarifa || '—'}</td>
                    <td>{formatearMonedaArs(e.totalCentavos)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3 style={{ marginTop: 24 }}>A pagar por profesional</h3>
          {reporte.porMedico.length === 0 ? (
            <p className="muted">No hay visitas en el período.</p>
          ) : (
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Profesional</th>
                  <th scope="col">Atenciones</th>
                  <th scope="col">Sin honorario</th>
                  <th scope="col">A pagar</th>
                </tr>
              </thead>
              <tbody>
                {reporte.porMedico.map((m) => (
                  <tr key={m.medicoId}>
                    <td>{m.medicoNombre}</td>
                    <td>{m.visitas}</td>
                    <td>{m.visitasSinHonorario || '—'}</td>
                    <td>{formatearMonedaArs(m.totalCentavos)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}
